import { useState, type FormEvent } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { api, ApiRequestError } from '@/lib/api'
import { safeOpsPath } from '@/components/ops/api'
import { FormError, errorMessage } from '@/components/app/states'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute('/ops_/login')({
  validateSearch: z.object({ redirect: z.string().optional().catch(undefined) }),
  head: () => ({ meta: [{ title: 'Ops sign in · Hedgehog' }, { name: 'robots', content: 'noindex' }] }),
  component: OpsLogin,
})

function OpsLogin() {
  const { redirect } = Route.useSearch()
  const router = useRouter()
  const [password, setPassword] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!password) {
      setError('Enter the ops password.')
      return
    }
    setPending(true)
    setError(null)
    try {
      await api('/ops/login', { method: 'POST', json: { password } })
      router.history.push(safeOpsPath(redirect))
    } catch (err) {
      setError(
        err instanceof ApiRequestError && (err.status === 401 || err.status === 403)
          ? 'Wrong password.'
          : err instanceof ApiRequestError && err.status === 429
            ? 'Too many attempts. Wait a minute and try again.'
            : errorMessage(err),
      )
      setPending(false)
    }
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background p-4 text-foreground">
      <form onSubmit={onSubmit} className="flex w-full max-w-xs flex-col gap-3 rounded-lg border border-border bg-card p-5" noValidate>
        <h1 className="text-base font-semibold">Hedgehog ops</h1>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ops-password">Password</Label>
          <Input
            id="ops-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? 'ops-login-error' : undefined}
          />
        </div>
        <div id="ops-login-error">
          <FormError message={error} />
        </div>
        <Button type="submit" disabled={pending}>
          {pending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </main>
  )
}
